import { useState, useEffect } from "react";
import { fetchAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { Save } from "lucide-react";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface LegalPage {
  page_key: string;
  title: string;
  title_ja: string;
  content: string;
  content_ja: string;
}

export default function LegalManager() {
  const [activeTab, setActiveTab] = useState("privacy_policy");
  const [data, setData] = useState<LegalPage>({ page_key: "", title: "", title_ja: "", content: "", content_ja: "" });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchData(activeTab);
  }, [activeTab]);

  const fetchData = async (key: string) => {
    setLoading(true);
    try {
      const result = await fetchAPI(`?type=legal&key=${key}`);
      if (result) {
        setData({
          page_key: result.page_key || key,
          title: result.title || "",
          title_ja: result.title_ja || "",
          content: result.content || "",
          content_ja: result.content_ja || ""
        });
      } else {
        // Fallback if not found (should be seeded)
        setData({ page_key: key, title: "", title_ja: "", content: "", content_ja: "" });
      }
    } catch (error) {
      console.error("Error fetching legal page:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const result = await fetchAPI("?type=legal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      
      if (result.status === "success") {
        toast({ title: "Saved", description: "Legal page updated successfully." });
      } else {
        throw new Error(result.message);
      }
    } catch (error: any) {
      toast({ variant: "destructive", title: "Error", description: error.message });
    } finally {
      setLoading(false);
    }
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link', 'clean']
    ],
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-serif font-bold text-foreground">Legal Pages</h1>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-[400px]">
          <TabsTrigger value="privacy_policy">Privacy Policy</TabsTrigger>
          <TabsTrigger value="terms_of_service">Terms of Service</TabsTrigger>
        </TabsList>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Edit {data.title || activeTab}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Page Title (English)</label>
                <Input 
                  value={data.title} 
                  onChange={(e) => setData({ ...data, title: e.target.value })} 
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Page Title (Japanese)</label>
                <Input 
                  value={data.title_ja} 
                  onChange={(e) => setData({ ...data, title_ja: e.target.value })} 
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Content (English)</label>
              <div className="bg-background">
                <ReactQuill 
                  theme="snow"
                  value={data.content} 
                  onChange={(value) => setData({ ...data, content: value })}
                  modules={modules}
                  className="h-[300px] mb-12"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Content (Japanese)</label>
              <div className="bg-background">
                <ReactQuill 
                  theme="snow"
                  value={data.content_ja} 
                  onChange={(value) => setData({ ...data, content_ja: value })}
                  modules={modules}
                  className="h-[300px] mb-12"
                />
              </div>
            </div>

            <div className="pt-4 flex justify-end">
              <Button onClick={handleSave} disabled={loading} className="gap-2">
                <Save className="w-4 h-4" />
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </Tabs>
    </div>
  );
}
